import { useEffect, useRef } from 'react';

// この距離を超えて動かして初めて「ドラッグ」と見なす。
// 超えるまでは何もしないので、通常のクリックでのタブ切替を邪魔しない。
const DRAG_THRESHOLD_PX = 5;

/**
 * 要素をマウスドラッグで横スクロールできるようにする(PC向け)。
 *
 * スクロール矢印を消したぶん、PC での横移動の手段を用意する。
 * タッチは元々ネイティブの横スワイプが効くので mouse のときだけ介入する。
 *
 * @param resolveScroller スクロールする要素を返す
 * @param revision        これが変わったら対象を取り直す
 */
export function useDragScroll(
  resolveScroller: () => HTMLElement | null | undefined,
  revision?: unknown,
): void {
  const resolveRef = useRef(resolveScroller);
  resolveRef.current = resolveScroller;

  useEffect(() => {
    const scroller = resolveRef.current();
    if (!scroller) return;

    let activePointerId: number | null = null;
    let startClientX = 0;
    let startScrollLeft = 0;
    let isDragging = false;

    const handlePointerDown = (event: PointerEvent) => {
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      if (scroller.scrollWidth <= scroller.clientWidth) return; // スクロールの余地なし
      activePointerId = event.pointerId;
      startClientX = event.clientX;
      startScrollLeft = scroller.scrollLeft;
      isDragging = false;
    };

    const handlePointerMove = (event: PointerEvent) => {
      if (activePointerId === null || event.pointerId !== activePointerId) return;
      const dx = event.clientX - startClientX;

      if (!isDragging) {
        if (Math.abs(dx) < DRAG_THRESHOLD_PX) return;
        isDragging = true;
        scroller.setPointerCapture(activePointerId);
        scroller.style.cursor = 'grabbing';
        scroller.style.userSelect = 'none'; // ドラッグ中にラベルが選択されるのを防ぐ
      }

      event.preventDefault();
      scroller.scrollLeft = startScrollLeft - dx;
    };

    const handlePointerUp = (event: PointerEvent) => {
      if (activePointerId === null || event.pointerId !== activePointerId) return;

      if (isDragging) {
        if (scroller.hasPointerCapture(activePointerId)) {
          scroller.releasePointerCapture(activePointerId);
        }
        scroller.style.cursor = '';
        scroller.style.userSelect = '';

        // pointerup の直後に飛ぶ click を1回だけ握り潰す。
        // これが無いと、指を離した位置にあるタブが選択されてしまう。
        // click が飛ばずに終わる場合もあるので、次のタスクで必ず外す。
        const suppressClick = (clickEvent: MouseEvent) => {
          clickEvent.preventDefault();
          clickEvent.stopPropagation();
        };
        scroller.addEventListener('click', suppressClick, { capture: true, once: true });
        window.setTimeout(() => {
          scroller.removeEventListener('click', suppressClick, { capture: true });
        }, 0);
      }

      activePointerId = null;
      isDragging = false;
    };

    scroller.addEventListener('pointerdown', handlePointerDown);
    // 捕捉が始まる前にポインタが外へ出ても追随できるよう、move/up は window で受ける
    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);
    window.addEventListener('pointercancel', handlePointerUp);

    return () => {
      scroller.removeEventListener('pointerdown', handlePointerDown);
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
      window.removeEventListener('pointercancel', handlePointerUp);
    };
  }, [revision]);
}
